import { useState, useRef, useEffect } from "react";
import DashboardHeader from "../../components/dashboard/HeaderDashboard";
import FormSection from "../../components/ui/FormSection";
import FormInput from "../../components/layout/FormInput";
import Button from "../../components/ui/Button";
import EditableTable from "../ui/EditableTable";
import StatusBadge from "../ui/StatusBadge";
import Modal from "../ui/Modal";
import EntityController from "@/network/controllers/entity.controller";
import UserController from "@/network/controllers/user.controller";
import { useSession } from "@/hooks/useSession";
import State, { STATE, CurrentEntityState } from "@state";
import InvitationController from "@/network/controllers/invitation.controller";
import NavigationService from "@services/navigation.service.js";
import AuthController from "@controllers/auth.controller.js";

export default function CROSettings() {
  const [activeTab, setActiveTab] = useState("Company information");
  const [showConfirmManagerModal, setShowConfirmManagerModal] = useState(false);
  const [managerToConfirm, setManagerToConfirm] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [updateUserError, setUpdateUserError] = useState(null);

  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(true);
  const [errorCompanyInfo, setErrorCompanyInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [errorManagers, setErrorManagers] = useState(null);
  const [errorUserInfo, setErrorUserInfo] = useState(null);
  const { user } = useSession();
  const currentUserId = user?.userId;
  const currentEntityId = STATE[CurrentEntityState.CURRENT_ENTITY_ID];

  // Company information state
  const [companyInfo, setCompanyInfo] = useState({
    name: "",
    street: "",
    number: "",
    country: "",
    city: "",
    postal_code: "",
    registration_number: "",
    contact_number: "",
    entity_specific_email: "",
  });

  const [userInfo, setUserInfo] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    contact_number: "",
    office_address_street: "",
    office_address_number: "",
    country: "",
    city: "",
    postal_code: "",
  });
  // Security state
  const [security, setSecurity] = useState({
    password: "",
    confirmPassword: "",
  });
  // Password validation state
  const [passwordError, setPasswordError] = useState("");

  const [companyManagers, setCompanyManagers] = useState([]);

  const [newManager, setNewManager] = useState({
    contactName: "",
    email: "",
  });
  const [isSendingManagerInvitation, setIsSendingManagerInvitation] =
    useState(false);

  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    const onStateChange = () => setRefresh((v) => v + 1);
    State.registerListener(CurrentEntityState.CURRENT_ENTITY_ID, onStateChange);
    return () => {
      State.unregisterListener(
        CurrentEntityState.CURRENT_ENTITY_ID,
        onStateChange
      );
    };
  }, []);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!currentEntityId) {
        setErrorCompanyInfo(
          "Entity ID is not available to fetch company information."
        );
        setLoadingCompanyInfo(false);
        return;
      }
      setLoadingCompanyInfo(true);
      setErrorCompanyInfo(null);
      try {
        const response = await EntityController.getEntityById(currentEntityId);
        console.log(
          "=== Company Info API response === CROSettings.jsx === key: 112233 ==="
        );
        console.dir(response, { depth: null, colors: true });
        console.log("=================================");

        if (response.success && response.data) {
          const companyData = response.data.data;
          const addressParts = (companyData.postal_address_1 || "").split(" ");
          const number = addressParts.length > 1 ? addressParts.pop() : "";
          const street = addressParts.join(" ");

          setCompanyInfo({
            name: companyData.name || "",
            street: street || "",
            number: number || "",
            country: companyData.country || "",
            city: companyData.city || "",
            postal_code: companyData.postal_code || "",
            registration_number: companyData.registration_number || "",
            contact_number: companyData.contact_number || "",
            entity_specific_email: companyData.entity_specific_email || "",
          });
          console.log(
            "=== Transformed Company Info State === CROSettings.jsx === key: 445566 ==="
          );
          console.dir(
            {
              name: companyData.name || "",
              street: street || "",
              number: number || "",
              country: companyData.country || "",
              city: companyData.city || "",
              postal_code: companyData.postal_code || "",
              registration_number: companyData.registration_number || "",
              contact_number: companyData.contact_number || "",
              entity_specific_email: companyData.entity_specific_email || "",
            },
            { depth: null, colors: true }
          );
          console.log("=================================");
        } else {
          setErrorCompanyInfo(
            response.message || "Failed to fetch company information."
          );
        }
      } catch (err) {
        console.error(
          "Network or unexpected error fetching company info:",
          err
        );
        setErrorCompanyInfo(
          "An unexpected error occurred while fetching company information."
        );
      } finally {
        setLoadingCompanyInfo(false);
      }
    };
    if (currentEntityId) {
      fetchCompanyInfo();
    } else {
      setLoadingCompanyInfo(false);
      setErrorCompanyInfo(
        "Entity ID not available to fetch company information."
      );
    }
  }, [currentEntityId]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!currentUserId) {
        setErrorUserInfo("L'ID utilisateur n'est pas disponible.");
        setLoadingUserInfo(false);
        return;
      }
      setLoadingUserInfo(true);
      setErrorUserInfo(null);
      try {
        const response = await UserController.getUserInfo(currentUserId);
        console.log(
          "Réponse complète de l'API (informations utilisateur) :",
          response
        );
        if (response?.success !== false) {
          const transformedData = {
            first_name: response.firstname || "",
            last_name: response.lastname || "",
            email_address: response.email || "",
            contact_number: response.phone_number || "",
            office_address_street:
              (response.postal_address_1 || "").split(", ")[0] || "",
            office_address_number:
              (response.postal_address_1 || "").split(", ")[1] || "",
            country: response.country || "",
            city: response.city || "",
            postal_code: response.zipcode || "",
          };
          setUserInfo(transformedData);
          console.log(
            "Données utilisateur transformées et définies dans l'état userInfo :",
            transformedData
          );
        } else {
          setErrorUserInfo(
            response.message ||
              "Échec de la récupération des informations utilisateur."
          );
        }
      } catch (err) {
        console.error(
          "Erreur réseau ou inattendue lors de la récupération des informations utilisateur :",
          err
        );
        setErrorUserInfo(
          "Une erreur inattendue est survenue lors de la récupération des informations utilisateur."
        );
      } finally {
        setLoadingUserInfo(false);
      }
    };
    if (currentUserId) {
      fetchUserInfo();
    } else {
      setLoadingUserInfo(false);
      setErrorUserInfo("User ID not available to fetch user information.");
    }
  }, [currentUserId]);

  useEffect(() => {
    const fetchCompanyManagers = async () => {
      if (activeTab === "Company management" && currentUserId) {
        setLoadingManagers(true);
        setErrorManagers(null);
        try {
          const response = await InvitationController.getInvitationsBySender();
          console.log(
            "=== Sent Invitations API response === CROSettings.jsx === key: 778899 ==="
          );
          console.dir(response, { depth: null, colors: true });
          console.log("=================================");

          if (response.success && Array.isArray(response.data)) {
            const mappedManagers = response.data.map((inv) => {
              const normalizedStatus =
                inv.status === "accepted" ? "active" : inv.status;
              return {
                id: inv.id,
                contactName: `${inv.firstname || ""} ${
                  inv.lastname || ""
                }`.trim(),
                email: inv.email,
                status: normalizedStatus,
              };
            });
            setCompanyManagers(mappedManagers);
          } else {
            console.error(
              "Failed to fetch sent invitations or response format is incorrect:",
              response.message
            );
            setErrorManagers(
              response.message || "Failed to load company managers."
            );
          }
        } catch (err) {
          console.error(
            "Network or unexpected error fetching sent invitations:",
            err
          );
          setErrorManagers(
            "An unexpected error occurred while fetching company managers."
          );
        } finally {
          setLoadingManagers(false);
        }
      }
    };
    if (activeTab === "Company management" && currentUserId) {
      fetchCompanyManagers();
    }
  }, [activeTab, currentUserId]);

  const handleConfirmClick = async (manager) => {
    if (confirmingId === manager.id) return;

    setConfirmingId(manager.id);
    try {
      const res = await InvitationController.confirmInvitation(manager.id);
      console.log("confirmInvitation (frontend) returned:", res);
      if (res.success) {
        setCompanyManagers((prev) =>
          prev.map((m) =>
            m.id === manager.id ? { ...m, status: "active" } : m
          )
        );
        alert("Invitation confirmed!");
      } else {
        alert(`Error confirming invitation: ${res.message}`);
      }
    } catch (err) {
      console.error("Unexpected error in handleConfirmClick:", err);
      alert("Unexpected error confirming invitation.");
    } finally {
      setConfirmingId(null);
    }
  };

  // Handle confirm manager invitation
  const handleConfirmManagerInvitation = () => {
    if (managerToConfirm) {
      setCompanyManagers((prev) =>
        prev.map((manager) =>
          manager.id === managerToConfirm.id
            ? { ...manager, status: "active" }
            : manager
        )
      );
      console.log("Manager invitation confirmed:", managerToConfirm);
    }
    setShowConfirmManagerModal(false);
    setManagerToConfirm(null);
  };

  // Handle company info input change

  const handleCompanyInfoChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle user info input change
  const handleUserInfoChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // -----------------------------------------------
  // TODO: Call blockchain to update user info
  // -----------------------------------------------
  // Update user information (CRO tab)
  const handleUpdateUserInfo = async () => {
    if (!currentUserId) {
      alert("User ID missing.");
      return;
    }

    setIsUpdatingUser(true);
    setUpdateUserError(null);

    const addressStr = [
      userInfo.office_address_street,
      userInfo.office_address_number,
    ]
      .map((s) => (s ?? "").trim())
      .filter(Boolean)
      .join(", ");

    const payload = {
      firstname: (userInfo.first_name ?? "").trim() || null,
      lastname: (userInfo.last_name ?? "").trim() || null,
      email: (userInfo.email_address ?? "").trim() || null,
      phone_number: (userInfo.contact_number ?? "").trim() || null,
      postal_address_1: addressStr || null,
      postal_address_2: addressStr || null,
      country: (userInfo.country ?? "").trim() || null,
      city: (userInfo.city ?? "").trim() || null,
      zipcode: (userInfo.postal_code ?? "").trim() || null,
    };

    const prefer = (next, prev) => {
      if (next === undefined || next === null) return prev;
      if (typeof next === "string" && next.trim() === "") return prev;
      return next;
    };

    try {
      const res = await UserController.updateUserInfo(currentUserId, payload);
      const { success, data, message } = res || {};
      if (!success) {
        const msg = message || "Update failed.";
        setUpdateUserError(msg);
        alert(msg);
        return;
      }

      const d = (data && Object.keys(data).length ? data : res) || {};

      let nextStreet = userInfo.office_address_street;
      let nextNumber = userInfo.office_address_number;

      if (
        typeof d.postal_address_1 === "string" &&
        d.postal_address_1.trim() !== ""
      ) {
        const parts = d.postal_address_1.split(",").map((s) => s.trim());

        if (parts[0] && parts[0].length) nextStreet = parts[0];
        if (parts[1] && parts[1].length) nextNumber = parts[1];
      }

      const nextState = {
        first_name: prefer(d.firstname, userInfo.first_name),
        last_name: prefer(d.lastname, userInfo.last_name),
        email_address: prefer(d.email, userInfo.email_address),
        contact_number: prefer(d.phone_number, userInfo.contact_number),
        office_address_street: nextStreet,
        office_address_number: nextNumber,
        country: prefer(d.country, userInfo.country),
        city: prefer(d.city, userInfo.city),
        postal_code: prefer(d.zipcode, userInfo.postal_code),
      };

      setUserInfo((prev) => ({ ...prev, ...nextState }));
      alert("User information updated successfully!");
    } catch (e) {
      console.error("Error updating user information:", e);
      setUpdateUserError("Unexpected error during update.");
      alert("Unexpected error during update.");
    } finally {
      setIsUpdatingUser(false);
    }
  };

  // Handle security input change
  const handleSecurityChange = (e) => {
    const { name, value } = e.target;
    setSecurity((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear password error when typing
    if (passwordError) {
      setPasswordError("");
    }
  };

  const handleNewManagerInputChange = (e) => {
    const { name, value } = e.target;
    setNewManager((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddManager = async () => {
    if (!newManager.contactName || !newManager.email) {
      alert("Please fill in all required fields for the new manager.");
      return;
    }

    setIsSendingManagerInvitation(true);

    try {
      const response = await InvitationController.sendProtectedInvitation({
        email: newManager.email,
        firstname: newManager.contactName.split(" ")[0],
        lastname: newManager.contactName.split(" ")[1] || "",
        invitation_type: "join_cro",
        send_by_user_id: currentUserId,
        entity_id: currentEntityId,
      });

      if (response.success) {
        const insertedInvitation = response.data;
        const newManagerWithId = {
          id: insertedInvitation.id || crypto.randomUUID(),
          contactName: newManager.contactName,
          email: newManager.email,
          status: "invited",
        };
        setCompanyManagers((prev) => [...prev, newManagerWithId]);
        setNewManager({ contactName: "", email: "" });
        alert("Study Manager invitation sent successfully!");
      } else {
        const errorMessage = response.message || "Failed to send invitation.";
        alert(`Error sending invitation: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error sending manager invitation:", error);
      alert("An unexpected error occurred while sending the invitation.");
    } finally {
      setIsSendingManagerInvitation(false);
    }
  };
  // Handle edit company manager
  const handleEditManager = (updatedManager) => {
    setCompanyManagers((prev) =>
      prev.map((manager) =>
        manager.id === updatedManager.id ? updatedManager : manager
      )
    );
    console.log("Manager updated:", updatedManager);
  };

  const handleUpdateCompanyInfo = async () => {
    try {
      const currentEntityId = STATE[CurrentEntityState.CURRENT_ENTITY_ID];
      const currentEntityData = STATE[CurrentEntityState.CURRENT_ENTITY_DATA];

      const newContact = {
        entityName: companyInfo.name,
        postalAddress: `${companyInfo.street} ${companyInfo.number}`,
        country: companyInfo.country,
        zipCode: companyInfo.postal_code,
        city: companyInfo.city,
        phoneNumber: companyInfo.contact_number,
        email: companyInfo.entity_specific_email,
        company_number: companyInfo.registration_number,
      };

      const response = await EntityController.updateEntityById(newContact);

      if (response?.success) {
        alert("Informations mises à jour avec succès.");
      } else {
        throw new Error(response?.message || "Erreur inconnue");
      }
    } catch (err) {
      console.error("Error updating entity:", err);
      alert("Erreur lors de la mise à jour.");
    }
  };

  // Handle delete company manager
  const handleDeleteManager = (managerToDelete) => {
    setCompanyManagers((prev) =>
      prev.filter((manager) => manager.id !== managerToDelete.id)
    );
    console.log("Manager deleted:", managerToDelete);
  };

  // Handle reorder company managers
  const handleReorderManagers = (oldIndex, newIndex) => {
    const newItems = [...companyManagers];
    const [movedItem] = newItems.splice(oldIndex, 1);
    newItems.splice(newIndex, 0, movedItem);
    setCompanyManagers(newItems);
    console.log("Managers reordered");
  };

  // Custom render function for manager actions
  const renderManagerActions = (item, onShowDeleteModal, onEdit) => {
    let statusElement = null;

    if (item.status === "pending") {
      statusElement = <StatusBadge type="invited">Invited</StatusBadge>;
    } else if (item.status === "joined") {
      statusElement = (
        <StatusBadge
          type="confirm"
          onClick={() => handleConfirmClick(item)}
          disabled={confirmingId === item.id}
          style={{ cursor: confirmingId === item.id ? "wait" : "pointer" }}
        >
          {confirmingId === item.id ? "Confirming…" : "Confirm"}
        </StatusBadge>
      );
    } else if (item.status === "active") {
      statusElement = <StatusBadge type="active">Active</StatusBadge>;
    }

    return (
      <div className="flex items-center space-x-2 justify-end">
        {statusElement}
        <button
          onClick={() => onShowDeleteModal(item)}
          className="text-gray-700 hover:text-gray-900 cursor-pointer"
          aria-label="Delete"
        >
          <img src="/icons/study/Bin.svg" className="w-6 h-6"></img>
        </button>
      </div>
    );
  };

  const managerColumns = [
    { header: "Contact name", key: "contactName", width: "40%" },
    { header: "Email address", key: "email", width: "40%" },
  ];

  // Update password
  const handleUpdatePassword = () => {
    // Validate passwords
    if (security.password.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    if (security.password !== security.confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    console.log("Updating password");

    // Reset form
    setSecurity({
      password: "",
      confirmPassword: "",
    });

    // Show success message
    alert("Password updated successfully!");
  };

  // Handle create study
  const handleCreateStudy = () => {
    NavigationService.navigateTo("/create-study");
  };

  // Handle sign out
  const handleSignOut = () => {
    AuthController.logout();
  };

  // Define the buttons array for the tabs
  const tabButtons = [
    "Company information",
    "Company management",
    "User information" /*  "Security" */,
  ];

  if (loadingCompanyInfo) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 lg:p-8">
        <DashboardHeader
          title="Settings"
          buttons={tabButtons}
          activeButton={activeTab}
          onButtonClick={setActiveTab}
        />
        <div className="flex-1 mt-2 rounded-xl bg-dashboard-bg1 flex items-center justify-center">
          <div className="text-xl text-gray-600">
            Company setting loading..;
          </div>
        </div>
      </div>
    );
  }

  if (errorCompanyInfo) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 lg:p-8">
        <DashboardHeader
          title="Settings"
          buttons={tabButtons}
          activeButton={activeTab}
          onButtonClick={setActiveTab}
        />
        <div className="flex-1 mt-2 rounded-xl bg-dashboard-bg1 flex items-center justify-center">
          <div className="text-xl text-red-600">
            Error loading setting.... {errorCompanyInfo}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden p-4 md:p-6 lg:p-8">
      <DashboardHeader
        title="Settings"
        buttons={tabButtons}
        activeButton={activeTab}
        onButtonClick={(tab) => setActiveTab(tab)}
      />
      <div className="flex-1 overflow-auto bg-dashboard-bg">
        <div className="flex-1 mt-2 rounded-xl  bg-dashboard-bg1">
          {/* Company Information Tab */}
          {activeTab === "Company information" && (
            <main className="p-3 sm:p-4 md:p-6">
              <FormSection title="Company information">
                <div className="flex flex-col md:flex-row">
                  {/* Form inputs - 2/3 width */}
                  <div className="md:w-2/3 pr-6">
                    <div className="mb-4">
                      <FormInput
                        label="Company name"
                        name="name"
                        value={companyInfo.name}
                        onChange={handleCompanyInfoChange}
                      />
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-grow">
                        <FormInput
                          label="Company street"
                          name="street"
                          value={companyInfo.street}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                      <div className="md:w-1/4">
                        <FormInput
                          label="Number"
                          name="number"
                          value={companyInfo.number}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="Country"
                          name="country"
                          value={companyInfo.country}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="City / Town"
                          name="city"
                          value={companyInfo.city}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Postal code"
                          name="postal_code"
                          value={companyInfo.postal_code}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="Company registration number"
                          name="registration_number"
                          value={companyInfo.registration_number}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Contact number"
                          name="contact_number"
                          value={companyInfo.contact_number}
                          onChange={handleCompanyInfoChange}
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <FormInput
                        label="Entity Specific Email"
                        name="entity_specific_email"
                        type="email"
                        value={companyInfo.entity_specific_email}
                        onChange={handleCompanyInfoChange}
                      />
                    </div>
                    <div className="p-6 flex justify-center">
                      <Button
                        onClick={handleUpdateCompanyInfo}
                        variant="addbutton"
                        className="w-[50%] text-white font-bold"
                      >
                        Update information
                      </Button>
                    </div>
                    <div className="h-20"></div>
                    <div className="h-10"></div>
                  </div>
                  {/* Logo section - 1/3 width */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-82 h-82 mt-4">
                      <img
                        src="/img/logo2.png"
                        alt="Oshimed Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </FormSection>
            </main>
          )}

          {/* Company Management Tab */}
          {activeTab === "Company management" && (
            <main className="p-3 sm:p-4 md:p-6">
              <FormSection title="Company management">
                <div className="flex flex-col md:flex-row">
                  {/* Form section - 2/3 width */}
                  <div className="md:w-2/3 pr-6">
                    <h3 className="text-lg font-semibold mb-4">Manager</h3>
                    <EditableTable
                      items={companyManagers}
                      columns={managerColumns}
                      onEdit={handleEditManager}
                      onDelete={handleDeleteManager}
                      onReorder={handleReorderManagers}
                      emptyMessage="No company managers have been added yet."
                      saveModalTitle="Save manager changes"
                      saveModalMessage="You are about to save the modifications made to the company managers."
                      cancelModalTitle="Discard manager changes"
                      cancelModalMessage="You are about to discard all modifications made to the company managers."
                      deleteModalTitle="Delete company manager"
                      deleteModalMessage="You are about to permanently delete this company manager."
                      renderActions={renderManagerActions}
                      firstRowEditable={true}
                    />

                    <h3 className="text-lg font-semibold mt-6 mb-4">
                      Add manager
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                      <FormInput
                        label="Contact name"
                        name="contactName"
                        value={newManager.contactName}
                        onChange={handleNewManagerInputChange}
                        placeholder="Enter contact name"
                      />
                      <FormInput
                        label="Email address"
                        name="email"
                        type="email"
                        value={newManager.email}
                        onChange={handleNewManagerInputChange}
                        placeholder="Enter email address"
                      />
                      <div className="md:col-start-3 md:row-start-1">
                        <Button
                          onClick={handleAddManager}
                          variant="addbutton"
                          className="w-full md:w-auto px-4 py-2 min-h-14"
                          disabled={isSendingManagerInvitation}
                        >
                          {isSendingManagerInvitation
                            ? "Sending..."
                            : "Send Invitation"}
                        </Button>
                      </div>
                    </div>
                    {/* <div className="p-6 flex justify-center">
                      <Button variant="addbutton" className="w-[50%] text-white font-bold">
                        Update information
                      </Button>
                    </div> */}
                    <div className="h-20"></div>
                    <div className="h-10"></div>
                  </div>

                  {/* Logo section - 1/3 width */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-82 h-82 mt-4">
                      <img
                        src="/img/logo2.png"
                        alt="Oshimed Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </FormSection>
            </main>
          )}

          {/* User Information Tab */}
          {activeTab === "User information" && (
            <main className="p-3 sm:p-4 md:p-6">
              <FormSection title="User information">
                <div className="flex flex-col md:flex-row">
                  {/* Form inputs - 2/3 width */}
                  <div className="md:w-2/3 pr-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="First name"
                          name="first_name"
                          value={userInfo.first_name}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Last name"
                          name="last_name"
                          value={userInfo.last_name}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="Email address"
                          name="email_address"
                          type="email"
                          value={userInfo.email_address}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Contact number"
                          name="contact_number"
                          type="tel"
                          value={userInfo.contact_number}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-grow">
                        <FormInput
                          label="Office address - Street"
                          name="office_address_street"
                          value={userInfo.office_address_street}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                      <div className="md:w-1/4">
                        <FormInput
                          label="Number"
                          name="office_address_number"
                          value={userInfo.office_address_number}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                      <div className="flex-1">
                        <FormInput
                          label="Country"
                          name="country"
                          value={userInfo.country}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="City / Town"
                          name="city"
                          value={userInfo.city}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                      <div className="flex-1">
                        <FormInput
                          label="Postal code"
                          name="postal_code"
                          value={userInfo.postal_code}
                          onChange={handleUserInfoChange}
                        />
                      </div>
                    </div>
                    <div className="p-6 flex justify-center">
                      <Button
                        onClick={handleUpdateUserInfo}
                        variant="addbutton"
                        className="w-[50%] text-white font-bold"
                        disabled={isUpdatingUser}
                      >
                        {isUpdatingUser ? "Saving..." : "Update information"}
                      </Button>
                    </div>
                    <div className="h-20"></div>
                    <div className="h-10"></div>
                  </div>
                  {/* Logo section - 1/3 width */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-82 h-82 mt-4">
                      <img
                        src="/img/logo2.png"
                        alt="Oshimed Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </FormSection>
            </main>
          )}

          {/* Security Tab */}
          {activeTab === "Security" && (
            <main className="p-3 sm:p-4 md:p-6">
              <FormSection title="Security">
                <div className="flex flex-col md:flex-row">
                  {/* Form inputs - 1/3 width */}
                  <div className="md:w-2/3 pr-6 ">
                    <h3 className="text-lg font-semibold mb-2">
                      Set a new password
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Your password secure your access to your account.
                    </p>
                    <p className="text-gray-600 mb-6">
                      If you loose your password, you can't retrieve it via your
                      email address.
                    </p>

                    <div className="mb-4 w-[50%]">
                      <FormInput
                        label="Password"
                        name="password"
                        type="password"
                        value={security.password}
                        onChange={handleSecurityChange}
                        placeholder="At least 8 characters"
                      />
                    </div>

                    <div className="mb-4 w-[50%]">
                      <FormInput
                        label="Re-enter password"
                        name="confirmPassword"
                        type="password"
                        value={security.confirmPassword}
                        onChange={handleSecurityChange}
                        placeholder="At least 8 characters"
                      />
                    </div>

                    {/* Password Error */}
                    {passwordError && (
                      <div className="mt-2 text-red-500">{passwordError}</div>
                    )}

                    <div className="mt-6 ">
                      <Button
                        onClick={handleUpdatePassword}
                        variant="addbutton"
                        className=" text-white font-bold w-[50%]"
                      >
                        Update password
                      </Button>
                    </div>
                    <div className="h-20"></div>
                    <div className="h-10"></div>
                  </div>

                  {/* Logo section - 1/3 width */}
                  <div className="md:w-1/3 flex flex-col items-center">
                    <div className="w-82 h-82 mt-4">
                      <img
                        src="/img/logo2.png"
                        alt="Oshimed Logo"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </FormSection>
            </main>
          )}
        </div>
      </div>
      <Modal
        isOpen={showConfirmManagerModal}
        onClose={() => setShowConfirmManagerModal(false)}
        title="Invitation sent"
        primaryAction={handleConfirmManagerInvitation}
        primaryButtonVariant="addbutton"
        primaryActionLabel="Confirm"
        secondaryAction={() => setShowConfirmManagerModal(false)}
        secondaryActionLabel="Back"
      >
        <p className="mb-4">
          Your colleague has been successfully invited to join the company.
        </p>
        <p className="mb-4">
          The invited user will receive an email with instructions to complete
          the onboarding process.
        </p>
      </Modal>
    </div>
  );
}
